/**
 * Auth hooks — wraps API calls for auth operations.
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getErrorMessage } from '@/lib/api'
import { fetchBootstrapAuth as fetchCurrentUser } from '@/lib/bootstrapQueries'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/components/ui/Toast'
import type { User, AuthTokens, LoginPayload, RegisterPayload } from '@/types'

export { fetchCurrentUser }

// ─── Fetch current user ───────────────────────────────────────────────────────

export function useCurrentUser(options?: { enabled?: boolean }) {
  const setUser = useAuthStore((s) => s.setUser)
  const setLoading = useAuthStore((s) => s.setLoading)

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchCurrentUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  })

  useEffect(() => {
    if (query.data) {
      setUser(query.data)
    } else if (!query.isLoading) {
      setUser(null)
      setLoading(false)
    }
  }, [query.data, query.isLoading, setUser, setLoading])

  return query
}

// ─── Login ────────────────────────────────────────────────────────────────────

export function useLogin() {
  const { setUser, setToken } = useAuthStore()
  const queryClient = useQueryClient()
  const { error: toastError } = useToast()

  return useMutation({
    mutationFn: async (payload: LoginPayload) => {
      const { data } = await api.post<{ data: AuthTokens & { user: User } }>(
        '/auth/login',
        payload,
      )
      return data.data
    },
    onSuccess: async (data) => {
      setToken(data.access_token)
      setUser(data.user)

      const { useCartStore } = await import('@/stores/cartStore')
      const sessionToken = useCartStore.getState().sessionToken
      if (sessionToken) {
        try {
          await api.post('/cart/merge', { session_token: sessionToken })
        } catch {
          // Merge failed silently — user still gets their authenticated cart
        }
        useCartStore.getState().setSessionToken('')
      }

      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['cart'] })
    },
    onError: (err) => {
      toastError('Login failed', getErrorMessage(err))
    },
  })
}

// ─── Register ─────────────────────────────────────────────────────────────────

export function useRegister() {
  const { setUser, setToken } = useAuthStore()
  const { error: toastError } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: RegisterPayload) => {
      const { data } = await api.post<{ data: AuthTokens & { user: User } }>(
        '/auth/register',
        payload,
      )
      return data.data
    },
    onSuccess: async (data) => {
      setToken(data.access_token)
      setUser(data.user)

      const { useCartStore } = await import('@/stores/cartStore')
      const sessionToken = useCartStore.getState().sessionToken
      if (sessionToken) {
        try {
          await api.post('/cart/merge', { session_token: sessionToken })
        } catch {
          // Merge failed silently
        }
        useCartStore.getState().setSessionToken('')
      }

      queryClient.invalidateQueries({ queryKey: ['cart'] })
    },
    onError: (err) => {
      toastError('Registration failed', getErrorMessage(err))
    },
  })
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function useLogout() {
  const { logout } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout')
    },
    onSettled: () => {
      logout()
      queryClient.clear()
    },
  })
}

// ─── Forgot / Reset password ──────────────────────────────────────────────────

export function useForgotPassword() {
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async (email: string) => {
      await api.post('/auth/forgot-password', { email })
    },
    onSuccess: () => {
      success('Reset link sent', 'Check your email for a password reset link.')
    },
    onError: (err) => {
      toastError('Error', getErrorMessage(err))
    },
  })
}

export function useResendVerification() {
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/email/resend')
    },
    onSuccess: () => {
      success('Email sent', 'A new verification link has been sent.')
    },
    onError: (err) => {
      toastError('Error', getErrorMessage(err))
    },
  })
}

export function useResetPassword() {
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async (payload: {
      token: string
      password: string
      password_confirmation: string
    }) => {
      await api.post('/auth/reset-password', {
        token: payload.token,
        password: payload.password,
        password_confirmation: payload.password_confirmation,
      })
    },
    onSuccess: () => {
      success('Password reset', 'You can now log in with your new password.')
    },
    onError: (err) => {
      toastError('Error', getErrorMessage(err))
    },
  })
}
